import { EChart, type EChartProps } from '@kbox-labs/react-echarts';
import type { ManipulateType } from 'dayjs';
import { useMemo } from 'react';

import type { VTimeSeriesChartDataTemplate } from './type';
import { makeFilledData } from './util';

export const VTimeSeriesChart = <T extends VTimeSeriesChartDataTemplate>({
  autoFillData,
  chartType,
  data,
  datePeriodUnit = 'day',
  dateRange,
  echartsProps,
  seriesOption,
}: {
  autoFillData?: boolean;
  chartType:
    | EChartsType
    | {
        [labelName in keyof T]: EChartsType;
      };
  data: T;
  /** @default 'day' */
  datePeriodUnit?: ManipulateType;
  dateRange: [DateLike, DateLike];
  echartsProps?: EChartProps;
  seriesOption?: {
    [labelName in keyof T]: Omit<SeriesOption, 'name' | 'type' | 'data'>;
  };
}) => {
  const labelNameList = Object.keys(data ?? {}) as Array<keyof T & string>;
  const filledData = useMemo(() => {
    if (!autoFillData) {
      return data;
    }

    return makeFilledData(data, dateRange, datePeriodUnit);
  }, [data, datePeriodUnit, dateRange, labelNameList]);

  const series = labelNameList.map(labelName => {
    const type =
      typeof chartType === 'string' ? chartType : chartType[labelName];
    const rowList = filledData[labelName] ?? [];

    return {
      name: labelName,
      type,
      data: rowList.map(row => row.value),
      ...seriesOption?.[labelName],
    } as SeriesOption;
  });

  const defaultLegend = {
    data: labelNameList,
    ...echartsProps?.legend,
  } as EChartProps['legend'];

  const defaultXAxis = {
    type: 'category',
    data: filledData[labelNameList[0]].map(row => row.timestamp),
    ...echartsProps?.xAxis,
  } as EChartProps['xAxis'];

  const defaultYAxis = {
    type: 'value',
    ...echartsProps?.yAxis,
  } as EChartProps['yAxis'];

  return (
    <EChart
      {...echartsProps}
      legend={defaultLegend}
      xAxis={defaultXAxis}
      yAxis={defaultYAxis}
      series={series}
    />
  );
};

VTimeSeriesChart.displayName = 'VTimeSeriesChart';
